import { createRouter, createWebHistory } from "vue-router"

import { useAuthStore } from '@/stores/auth'
import AdminCreateView from "@/views/admin/AdminCreateView.vue"
import AdminListView from "@/views/admin/AdminListView.vue"
import AffiliateCreateView from "@/views/affiliate/AffiliateCreateView.vue"
import AffiliateListView from "@/views/affiliate/AffiliateListView.vue"
import AffiliateUpdateView from "@/views/affiliate/AffiliateUpdateView.vue"
import AuctionCreateView from "@/views/auction/AuctionCreateView.vue"
import AuctionListView from "@/views/auction/AuctionListView.vue"
import AuctionUpdateView from "@/views/auction/AuctionUpdateView.vue"
import AuctionAttachmentCreateView from "@/views/auction_attachment/AuctionAttachmentCreateView.vue"
import AuctionAttachmentListView from "@/views/auction_attachment/AuctionAttachmentListView.vue"
import AuctionCalendarCreateView from "@/views/auction_calendar/AuctionCalendarCreateView.vue"
import AuctionCalendarListView from "@/views/auction_calendar/AuctionCalendarListView.vue"
import AuctionCalendarUpdateView from "@/views/auction_calendar/AuctionCalendarUpdateView.vue"
import BottlerCreateView from "@/views/bottler/BottlerCreateView.vue"
import BottlerListView from "@/views/bottler/BottlerListView.vue"
import BottlerUpdateView from "@/views/bottler/BottlerUpdateView.vue"
import CategoryCreateView from "@/views/category/CategoryCreateView.vue"
import CategoryListView from "@/views/category/CategoryListView.vue"
import CategoryUpdateView from "@/views/category/CategoryUpdateView.vue"
import CompanyCategoryView from "@/views/company/CompanyCategoryView.vue"
import CompanyCompanyGroupView from "@/views/company/CompanyCompanyGroupView.vue"
import CompanyCreateView from "@/views/company/CompanyCreateView.vue"
import CompanyListView from "@/views/company/CompanyListView.vue"
import CompanyUpdateView from "@/views/company/CompanyUpdateView.vue"
import CompanyWasteCodeView from "@/views/company/CompanyWasteCodeView.vue"
import CompanyGroupCreateView from "@/views/company_group/CompanyGroupCreateView.vue"
import CompanyGroupListView from "@/views/company_group/CompanyGroupListView.vue"
import CompanyGroupUpdateView from "@/views/company_group/CompanyGroupUpdateView.vue"
import DashboardView from "@/views/DashboardView.vue"
import HomeView from "@/views/HomeView.vue"
import LoginView from "@/views/LoginView.vue"
import LogoutView from "@/views/LogoutView.vue"
import LotCreateView from "@/views/lot/LotCreateView.vue"
import LotDetailView from "@/views/lot/LotDetailView.vue"
import LotListView from "@/views/lot/LotListView.vue"
import LotSummaryView from "@/views/lot/LotSummaryView.vue"
import LotUpdateView from "@/views/lot/LotUpdateView.vue"
import LotRedistributeSummaryView from "@/views/lot_redistribute/LotRedistributeSummaryView.vue"
import ProductCreateView from "@/views/product/ProductCreateView.vue"
import ProductListView from "@/views/product/ProductListView.vue"
import ProductUpdateView from "@/views/product/ProductUpdateView.vue"
import ProfileView from "@/views/ProfileView.vue"
import SelectionSiteCreateView from "@/views/selection_site/SelectionSiteCreateView.vue"
import SelectionSiteListView from "@/views/selection_site/SelectionSiteListView.vue"
import SelectionSiteUpdateView from "@/views/selection_site/SelectionSiteUpdateView.vue"
import UserCreateView from "@/views/user/UserCreateView.vue"
import UserListView from "@/views/user/UserListView.vue"
import UserUpdateView from "@/views/user/UserUpdateView.vue"
import ViewerCreateView from "@/views/viewer/ViewerCreateView.vue"
import ViewerListView from "@/views/viewer/ViewerListView.vue"
import WasteCodeCreateView from "@/views/waste_code/WasteCodeCreateView.vue"
import WasteCodeListView from "@/views/waste_code/WasteCodeListView.vue"

const guest = function (to, from, next) {
  const authStore = useAuthStore()
  if (!authStore.isAuthenticated) {
    next();
  } else {
    next('/dashboard');
  }
}

const auth = function (to, from, next) {
  const authStore = useAuthStore()
  if (authStore.isAuthenticated) {
    next();
  } else {
    next('/logout');
  }
}

const middleware = function(method) {
  return function (to, from, next) {
    const authStore = useAuthStore()
    if (authStore[method]) {
      next();
    } else {
      next('/logout');
    }
  }
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "home",
      component: HomeView,
      beforeEnter: guest
    }, {
      path: "/login",
      name: "login",
      component: LoginView,
      beforeEnter: guest
    }, {
      path: "/logout",
      name: "logout",
      component: LogoutView
    }, {
      path: "/dashboard",
      name: "dashboard",
      component: DashboardView,
      beforeEnter: auth
    }, {
      path: "/profilo",
      name: "profile",
      component: ProfileView,
      beforeEnter: middleware('isAdmin')  
    }, {
      path: "/utenti",
      name: "users",
      component: UserListView,
      beforeEnter: middleware('isAdmin')
    }, {
      path: "/utenti/crea",
      name: "user_create",
      component: UserCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/utenti/:id",
      name: "user_update",
      component: UserUpdateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/visualizzatori",
      name: "viewers",
      component: ViewerListView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/visualizzatori/crea",
      name: "viewers_create",
      component: ViewerCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/amministratori",
      name: "admins",
      component: AdminListView,
      beforeEnter: middleware('canManageAdmin')
    }, {
      path: "/amministratori/crea",
      name: "admins_create",
      component: AdminCreateView,
      beforeEnter: middleware('canManageAdmin')
    },{
      path: "/partecipanti",
      name: "companies",
      component: CompanyListView,
      beforeEnter: middleware('isAdmin')
    }, {
      path: "/partecipanti/crea",
      name: "company_create",
      component: CompanyCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/partecipanti/:id",
      name: "company_update",
      component: CompanyUpdateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/partecipanti/:id/categorie",
      name: "company_category",
      component: CompanyCategoryView,
      beforeEnter: auth
    }, {
      path: "/partecipanti/:id/codici-rifiuto",
      name: "company_waste_code",
      component: CompanyWasteCodeView,
      beforeEnter: auth
    }, {
      path: "/partecipanti/:id/gruppi-partecipanti",
      name: "company_company_group",
      component: CompanyCompanyGroupView,
      beforeEnter: auth
    }, {
      path: "/gruppi-partecipanti",
      name: "company_groups",
      component: CompanyGroupListView,
      beforeEnter: middleware('isAdmin')
    }, {
      path: "/gruppi-partecipanti/crea",
      name: "company_group_create",
      component: CompanyGroupCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/gruppi-partecipanti/:id",
      name: "company_group_update",
      component: CompanyGroupUpdateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/accreditati",
      name: "affiliates",
      component: AffiliateListView,
      beforeEnter: middleware('isAdmin')
    },  {
      path: "/accreditati/crea",
      name: "affiliate_create",
      component: AffiliateCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/accreditati/:id",
      name: "affiliate_update",
      component: AffiliateUpdateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/imbottigliatori",
      name: "bottlers",
      component: BottlerListView,
      beforeEnter: middleware('isAdmin')
    },  {
      path: "/imbottigliatori/crea",
      name: "bottler_create",
      component: BottlerCreateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/imbottigliatori/:id",
      name: "bottler_update",
      component: BottlerUpdateView,
      beforeEnter: middleware('canManageUser')
    }, {
      path: "/centri-selezione",
      name: "selection_sites",
      component: SelectionSiteListView,
      beforeEnter: auth
    }, {
      path: "/centri-selezione/crea",
      name: "selection_sites_create",
      component: SelectionSiteCreateView,
      beforeEnter: middleware('canManageAuction')
    }, {
      path: "/centri-selezione/:id",
      name: "selection_sites_update",
      component: SelectionSiteUpdateView,
      beforeEnter: middleware('canManageAuction')
    }, {
      path: "/aste",
      name: "auctions",
      component: AuctionListView,
      beforeEnter: auth
    }, {
      path: "/aste/crea",
      name: "auction_create",
      component: AuctionCreateView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/aste/:id",
      name: "auction_update",
      component: AuctionUpdateView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/calendario-aste",
      name: "auction_calendars",
      component: AuctionCalendarListView,
      beforeEnter: auth
    }, {
      path: "/calendario-aste/crea",
      name: "auction_calendar_create",
      component: AuctionCalendarCreateView,
      beforeEnter: middleware('canManageAuction')
    }, {
      path: "/calendario-aste/:id",
      name: "auction_calendar_update",
      component: AuctionCalendarUpdateView,
      beforeEnter: middleware('canManageAuction')
    }, {
      path: "/aste/:auctionId/lotti",
      name: "lots",
      component: LotListView,
      beforeEnter: auth
    }, {
      path: "/aste/:auctionId/lotti/crea",
      name: "lot_create",
      component: LotCreateView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/aste/:auctionId/lotti/visualizza/:id",
      name: "lot_detail",
      component: LotDetailView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/aste/:auctionId/lotti/:id",
      name: "lot_update",
      component: LotUpdateView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/aste/:auctionId/visualizza-lotti-redistribuzione",
      name: "redistribute_lots_summary",
      component: LotRedistributeSummaryView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/aste/:auctionId/visualizza-lotti",
      name: "lots_summary",
      component: LotSummaryView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    },{
      path: "/aste/:auctionId/allegati",
      name: "auction_attachments",
      component: AuctionAttachmentListView,
      beforeEnter: auth
    }, {
      path: "/aste/:auctionId/allegati/crea",
      name: "auction_attachment_create",
      component: AuctionAttachmentCreateView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/prodotti",
      name: "products",
      component: ProductListView,
      beforeEnter: middleware('canManageAuctionOrCebis')
    }, {
      path: "/prodotti/crea",
      name: "product_create",
      component: ProductCreateView,
      beforeEnter: middleware('canManagePlatform')
    }, {
      path: "/prodotti/:id",
      name: "product_update",
      component: ProductUpdateView,
      beforeEnter: middleware('canManagePlatform')
    }, {
      path: "/categorie",
      name: "categories",
      component: CategoryListView,
      beforeEnter: auth
    }, {
      path: "/categorie/crea",
      name: "category_create",
      component: CategoryCreateView,
      beforeEnter: middleware('canManagePlatform')
    }, {
      path: "/categorie/:id",
      name: "category_update",
      component: CategoryUpdateView,
      beforeEnter: middleware('canManagePlatform')
    }, {
      path: "/codici-rifiuto",
      name: "waste_codes",
      component: WasteCodeListView,
      beforeEnter: auth
    }, {
      path: "/codici-rifiuto/crea",
      name: "waste_code_create",
      component: WasteCodeCreateView,
      beforeEnter: middleware('canManagePlatform')
    }
  ],
});

export default router;